/**
 * Project - fasthelp5.fa2chat-product
 *
 * @desc Window関連ユーティリティ
 *
 * @author genka
 * @copyright Copyright 2018 TechMatrix Corporation, Inc. All rights reserved.
 */
import Vue from 'vue'

const Dom = require('element-ui/lib/utils/dom')

export default new class {
  static addEventListener = window.addEventListener || window.attachEvent
  static removeEventListener = window.removeEventListener || window.detachEvent

  /**
   * チャット画面を別ウィンドウで開いているか
   * @return {Boolean}
   */
  isSeparateWindow() {
    return window.parent === window
  }

  /**
   * チャット画面の表示モード（mobile or pc）を取得します。
   * @return {String} mobile or pc
   */
  getScreenMode() {
    const ua = window.navigator.userAgent.toLowerCase()
    if ((ua.indexOf('iphone') !== -1 || ua.indexOf('ipod') !== -1) ||
      (ua.indexOf('ipad') !== -1) ||
      (ua.indexOf('android') !== -1) ||
      (ua.indexOf('windows') !== -1 && ua.indexOf('phone') !== -1)) {
      return 'mobile'
    }
    return 'pc'
  }

  /**
   * チャット画面の表示モードがmobileか
   * @return {Boolean}
   */
  isMobileScreenMode() {
    return this.getScreenMode() === 'mobile'
  }

  /**
   * チャット画面の表示モードがpcか
   * @return {Boolean}
   */
  isPcScreenMode() {
    return this.getScreenMode() === 'pc'
  }

  /**
   * 画面モードを更新します。
   * @param  {String} screenMode
   */
  updateScreenMode(screenMode) {
    if (screenMode === 'pc') {
      Dom.removeClass(document.body, 'fc-screen-mode-mobile')
    } else if (screenMode === 'mobile') {
      Dom.addClass(document.body, 'fc-screen-mode-mobile')
    }
  }

  /**
   * 親Windowからのメッセージ受信イベントリスナーを追加します。
   * @param {string} messageType
   * @param {Function} listener
   * @param {boolean} once
   */
  addMessageEventListener(messageType, listener, once) {
    addEventListener('message', function (event) {
      if (event.data.type === messageType) {
        listener(event)
        if (once) {
          removeEventListener('message', listener)
        }
      }
    })
  }

  /**
   * 親Windowにメッセージ送信します。
   * @param  {Object}   data 送信データ
   */
  postMessageToParent(data) {
    window.parent.postMessage(data, Vue.prototype.$config.parentOrigin)
  }

  /**
   * 親Windowにドラッグ開始メッセージを送信します。
   * @param  {Event}   mousedownevent マウスダウンイベント
   */
  postDragStartMessageToParent(mousedownevent) {
    this.postMessageToParent({
      type: 'dragStart',
      position: {
        x: mousedownevent.pageX,
        y: mousedownevent.pageY
      }
    })
  }

  /**
   * 親Windowにフレーム初期化メッセージを送信します。
   * @param  {Object}   data フレーム設定情報
   */
  postInitFastchatFrameMessageToParent(fastchatFrameConfig) {
    this.postMessageToParent({
      type: 'initFastchatFrame',
      fastchatFrameConfig: fastchatFrameConfig
    })
  }

  /**
   * 親Windowにチャット画面表示メッセージを送信します。
   */
  postShowChatWindowMessageToParent() {
    this.postMessageToParent({
      type: 'showChatWindow'
    })
  }

  /**
   * 親Windowにチャット開始画面表示メッセージを送信します。
   */
  postShowChatStarterMessageToParent() {
    this.postMessageToParent({
      type: 'showChatStarter'
    })
  }

  /**
   * 親Windowに通知ラベルメッセージを送信します。
   */
  postNoticeLabelMessageToParent(label) {
    this.postMessageToParent({
      type: 'noticeLabel',
      label: label
    })
  }

  /**
   * 親Windowにチャット画面別ウィンドウオープンメッセージを送信します。
   */
  postOpenSeparateChatWindowMessageToParent() {
    this.postMessageToParent({
      type: 'openSeparateChatWindow'
    })
  }

  /**
   * 利用Webフォーム設定番号のWebフォーム画面を開く
   * @param {String} callSpecificKey 対象コール特定キー
   */
  openWebFormWindow(callSpecificKey, webFormSettingId) {
    const webFormUrl = Vue.prototype.$t('{webFormParentPath}/form{webFormSettingId}.html?specificKey={callSpecificKey}', {
      webFormParentPath: Vue.prototype.$config.webFormParentPath,
      webFormSettingId: webFormSettingId || Vue.prototype.$config.webFormSettingId,
      callSpecificKey: callSpecificKey
    })
    window.open(webFormUrl, '_blank')
  }

  /**
   * 別ウィンドウを開く
   * @param {String} url URL
   */
  openWindow(url) {
    window.open(url, '_blank')
  }

  /**
   * GETパラメータのオブジェクトを取得します。
   * @return {[Object]}
   */
  getQueryParamObject() {
    let queryString = window.location.search
    const queryObject = {}
    if (queryString) {
      queryString = queryString.substring(1)
      let parameters = queryString.split('&')
      for (let i = 0; i < parameters.length; i++) {
        let param = parameters[i].split('=')
        let paramName = decodeURIComponent(param[0])
        let paramValue = decodeURIComponent(param[1])
        queryObject[paramName] = paramValue
      }
    }
    return queryObject
  }

  /**
   * FastChatが配置されているwindowを取得します。
   * @return {[Object]}
   */
  getEmbeddedFastChatWindow() {
    if (window.opener) {
      // 別タブで開いた場合
      return window.opener.parent
    }
    return window.parent
  }

  /**
   * 画像プレビューのウィンドウを開きます
   * @param {String} service
   * @param {String} title
   */
  openPreviewImageWindow(service, title) {
    let serviceUrl = Vue.prototype.$config.serviceServer + '/' + service
    const previewWindow = window.open('about:blank', '_blank')
    previewWindow.document.open()
    previewWindow.document.writeln('<html style="height: 100%;">')
    previewWindow.document.writeln('<head>')
    previewWindow.document.writeln('<title>' + title + '</title>')
    previewWindow.document.writeln('<script type="text/javascript">')
    previewWindow.document.writeln('function bodyLoadHandler(){')
    previewWindow.document.writeln('  var xhr = new XMLHttpRequest();')
    previewWindow.document.writeln('  xhr.open("GET", "' + serviceUrl + '");')
    previewWindow.document.writeln('  xhr.responseType = "blob";')
    previewWindow.document.writeln('  xhr.onload = function() {')
    previewWindow.document.writeln('    if(this.status == 200 ){')
    previewWindow.document.writeln('      var blob = this.response;')
    previewWindow.document.writeln('      var image = document.getElementById("image");')
    previewWindow.document.writeln('      image.src = window.URL.createObjectURL(blob);')
    previewWindow.document.writeln('      var loadingLabel = document.getElementById("loadingLabel");')
    previewWindow.document.writeln('      loadingLabel.style.display = "none";')
    previewWindow.document.writeln('      image.style.display = "";')
    previewWindow.document.writeln('    }')
    previewWindow.document.writeln('  };')
    previewWindow.document.writeln('  xhr.send();')
    previewWindow.document.writeln('}')
    previewWindow.document.writeln('</script>')
    previewWindow.document.writeln('</head>')
    previewWindow.document.writeln('<body style="margin: 0px; background: #0e0e0e; height: 100%; overflow: hidden;" onload="bodyLoadHandler()">')
    previewWindow.document.writeln('<div style="width:100%; height: 100%; display: flex; justify-content: center; align-items: center;">')
    previewWindow.document.writeln('<div id="loadingLabel" style="color: white">' + Vue.prototype.$t('読込中...') + '</div>')
    previewWindow.document.writeln('<img id="image" style="display:none; max-width:100%; max-height:100%; width:auto; height:auto;">')
    previewWindow.document.writeln('</div>')
    previewWindow.document.writeln('</body>')
    previewWindow.document.writeln('</html>')
    previewWindow.document.close()
  }

  /**
   * 動画プレビューのウィンドウを開きます
   * @param {String} service
   * @param {String} title
   * @param {String} videoType
   */
  openPreviewVideoWindow(service, title, videoType) {
    let serviceUrl = Vue.prototype.$config.serviceServer + '/' + service
    const previewWindow = window.open('about:blank', '_blank')
    previewWindow.document.open()
    previewWindow.document.writeln('<html style="height: 100%;">')
    previewWindow.document.writeln('<head>')
    previewWindow.document.writeln('<meta http-equiv="X-UA-Compatible" content="IE=edge">')
    previewWindow.document.writeln('<title>' + title + '</title>')
    previewWindow.document.writeln('<script type="text/javascript">')
    previewWindow.document.writeln('var blob = null;')
    previewWindow.document.writeln('var blobUrl = null;')
    previewWindow.document.writeln('var video = null;')
    previewWindow.document.writeln('function bodyLoadHandler(){')
    previewWindow.document.writeln('  var xhr = new XMLHttpRequest();')
    previewWindow.document.writeln('  xhr.open("GET", "' + serviceUrl + '");')
    previewWindow.document.writeln('  xhr.responseType = "blob";')
    previewWindow.document.writeln('  xhr.onload = function() {')
    previewWindow.document.writeln('    if(this.status == 200 ){')
    previewWindow.document.writeln('      blob = this.response;')
    previewWindow.document.writeln('      blobUrl = window.URL.createObjectURL(blob);')
    previewWindow.document.writeln('      video = document.getElementById("video");')
    previewWindow.document.writeln('      video.src = blobUrl;')
    previewWindow.document.writeln('      var loadingLabel = document.getElementById("loadingLabel");')
    previewWindow.document.writeln('      loadingLabel.style.display = "none";')
    previewWindow.document.writeln('      video.style.display = "";')
    previewWindow.document.writeln('    }')
    previewWindow.document.writeln('  };')
    previewWindow.document.writeln('  xhr.send();')
    previewWindow.document.writeln('}')
    previewWindow.document.writeln('function videoErrorHandler(){')
    previewWindow.document.writeln('  var videowrapper = document.getElementById("videowrapper");')
    previewWindow.document.writeln('  if(!video.canPlayType("' + videoType + '")){')
    previewWindow.document.writeln('    updateInvalidVideoView();')
    previewWindow.document.writeln('    return;')
    previewWindow.document.writeln('  }')
    previewWindow.document.writeln('  videowrapper.removeChild(video);')
    previewWindow.document.writeln('  video = document.createElement("video");')
    previewWindow.document.writeln('  video.id = "video"')
    previewWindow.document.writeln('  video.style["max-width"] = "100%"')
    previewWindow.document.writeln('  video.style["max-height"] = "100%"')
    previewWindow.document.writeln('  video.style["width"] = "auto"')
    previewWindow.document.writeln('  video.style["height"] = "auto"')
    previewWindow.document.writeln('  video.autoplay = "true"')
    previewWindow.document.writeln('  video.muted = "true"')
    previewWindow.document.writeln('  video.playsinline = "true"')
    previewWindow.document.writeln('  video.controls = "true"')
    previewWindow.document.writeln('  var source = document.createElement("source");')
    previewWindow.document.writeln('  source.onerror = updateInvalidVideoView;')
    previewWindow.document.writeln('  source.type = "' + videoType + '";')
    previewWindow.document.writeln('  source.src = blobUrl;')
    previewWindow.document.writeln('  video.appendChild(source);')
    previewWindow.document.writeln('  videowrapper.appendChild(video);')
    previewWindow.document.writeln('}')
    previewWindow.document.writeln('function updateInvalidVideoView(){')
    previewWindow.document.writeln('  var downloadLink = document.getElementById("downloadLink");')
    previewWindow.document.writeln('  video.style.display = "none";')
    previewWindow.document.writeln('  downloadLink.style.display = "";')
    previewWindow.document.writeln('}')
    previewWindow.document.writeln('function downloadLinkClickHandler(){')
    previewWindow.document.writeln('  if (window.navigator.msSaveBlob) {')
    previewWindow.document.writeln('    window.navigator.msSaveBlob(blob, "' + title + '");')
    previewWindow.document.writeln('  } else {')
    previewWindow.document.writeln('    let link = document.createElement("a");')
    previewWindow.document.writeln('    document.body.appendChild(link);')
    previewWindow.document.writeln('    link.href = blobUrl;')
    previewWindow.document.writeln('    link.download = "' + title + '";')
    previewWindow.document.writeln('    link.click();')
    previewWindow.document.writeln('    document.body.removeChild(link);')
    previewWindow.document.writeln('  }')
    previewWindow.document.writeln('}')
    previewWindow.document.writeln('</script>')
    previewWindow.document.writeln('</head>')
    previewWindow.document.writeln('<body style="margin: 0px; background: #0e0e0e; height: 100%; overflow: hidden;" onload="bodyLoadHandler()">')
    previewWindow.document.writeln('<div id="videowrapper" style="width:100%; height: 100%; display: flex; justify-content: center; align-items: center;">')
    previewWindow.document.writeln('<div id="loadingLabel" style="color: white">' + Vue.prototype.$t('読込中...') + '</div>')
    previewWindow.document.writeln('<video id="video" preload="auto" style="display:none; max-width:100%; max-height:100%; width:auto; height:auto;" onerror="videoErrorHandler()" playsinline muted autoplay controls>')
    previewWindow.document.writeln('</video>')
    previewWindow.document.writeln('<a id="downloadLink" style="display:none; color: white;" href="javascript:downloadLinkClickHandler()">' + Vue.prototype.$t('お使いのブラウザでは再生できないため、こちらをクリックして動画ファイルをダウンロードしてください。') + '</a>')
    previewWindow.document.writeln('</div>')
    previewWindow.document.writeln('</body>')
    previewWindow.document.writeln('</html>')
    previewWindow.document.close()
  }
}()



// WEBPACK FOOTER //
// ./src/utils/WindowUtil.js